import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Output,
  ViewChild
} from '@angular/core';
import {ElasticParserType, ElasticValidatorType} from 'asax-elastic';
import {AsaxFrequencyViewService, Extremes, HeaderFilter} from 'asax-common';
import {AsaxBookmarkingService} from 'asax-bookmarking';
import {FrequencyViewChartFilterService} from '../frequency-view-chart-filter.service';
import {filter, tap} from 'rxjs/operators';
import {BehaviorSubject, distinctUntilChanged, Subject} from 'rxjs';
import {RedshiftMode} from '../frequency-view-chart-filter-redshift/frequency-view-chart-filter-redshift.component';

export interface DropdownItem {
  value: string;
  text: string;
  selected?: boolean;
}

export interface ChartInput {
  readonly id: string;
  readonly label: string;
  readonly elasticParserType: ElasticParserType;
  readonly elasticValidatorType?: ElasticValidatorType;
  readonly dropdownItems?: DropdownItem[];
}

export interface ChartFilter {
  readonly id: string;
  readonly label?: string;
  readonly value: any;
  readonly valueParsed?: any;
  readonly isInvalid?: boolean;
  readonly elasticParserType?: ElasticParserType;
}

@Component({
  selector: 'asax-frequency-view-frequency-view-chart-filters-container',
  templateUrl: './frequency-view-chart-filters-container.component.html',
  styleUrls: ['frequency-view-chart-filters-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FrequencyViewChartFiltersContainerComponent implements AfterViewInit {
  @ViewChild('chartFiltersContainer', {static: true}) chartFiltersContainer: ElementRef;

  @Output() outHeight = new EventEmitter<number>();
  
  height = new Subject<number>();
  displayResetBoundariesButton: boolean = false;
  extremes: Extremes;

  linesChartInput: ChartInput = {
    id: 'lines',
    label: 'Lines',
    elasticParserType: 'regex',
    elasticValidatorType: 'regex'
  };

  moleculesChartInput: ChartInput = {
    id: 'molecules',
    label: 'Molecules',
    elasticParserType: 'dropdown-items',
    dropdownItems: [
      {value: 'top20', text: 'Top 20 list'},
      {value: 'planet', text: 'Planetary Atmosphere'},
      {value: 'ism_hotcore', text: 'Hot Cores'},
      {value: 'ism_darkcloud', text: 'Dark Clouds'},
      {value: 'ism_diffusecloud', text: 'Diffuse Clouds'},
      {value: 'comet', text: 'Comets'},
      {value: 'AGB_PPN_PN', text: 'AGB/PPN/PN'},
      {value: 'extragalactic', text: 'Extragalactic'}
    ]
  };

  redshiftChartInput: ChartInput = {
    id: 'redshift',
    label: 'Redshift',
    elasticParserType: 'number',
    elasticValidatorType: 'number'
  };

  redshiftFilterValue = new BehaviorSubject<string>(undefined);
  linesFilterValue = new BehaviorSubject<string>(undefined);
  moleculesFilterValue = new BehaviorSubject<string>(undefined);
  redshiftEstimated = new BehaviorSubject<number>(undefined);

  redshiftMode: RedshiftMode;

  constructor(private readonly asaxBookmarkingService: AsaxBookmarkingService,
              private readonly asaxFrequencyViewService: AsaxFrequencyViewService,
              private readonly frequencyViewChartFilterService: FrequencyViewChartFilterService,
              private readonly cdr: ChangeDetectorRef) {
    this.height.asObservable().pipe(
      distinctUntilChanged(),
      tap(height => this.outHeight.emit(height))
    ).subscribe();

    this.asaxFrequencyViewService.getZoomExtremes().pipe(
      tap(extremes => this.onExtremes(extremes))
    ).subscribe();

    this.asaxFrequencyViewService.getExtremes().pipe(
      tap(extremes => this.extremes = extremes),
      tap(extremes => this.onExtremes(extremes))
    ).subscribe();
  }

  ngDoCheck(): void {
    this.height.next(this.chartFiltersContainer.nativeElement.clientHeight);
  }

  private onExtremes(extremes: Extremes) {
    if (this.extremes &&
        (this.extremes.from !== extremes.from ||
            this.extremes.to !== extremes.to)) {
        this.displayResetBoundariesButton = true;
    } else {
        this.displayResetBoundariesButton = false;
    }
    this.cdr.markForCheck();
  } 

  ngAfterViewInit(): void {
    this.asaxBookmarkingService.getQueryParam('redshift').pipe(
      filter(queryParamValue => queryParamValue !== undefined),
      filter(queryParamValue => queryParamValue !== this.frequencyViewChartFilterService.getChartFilter('redshift')?.value),
      tap(value => this.redshiftFilterValue.next(value))
    ).subscribe();

    this.asaxBookmarkingService.getQueryParam('lines').pipe(
      filter(queryParamValue => queryParamValue !== undefined),
      filter(queryParamValue => queryParamValue !== this.frequencyViewChartFilterService.getChartFilter('lines')?.value),
      tap(value => this.linesFilterValue.next(value))
    ).subscribe();

    this.asaxBookmarkingService.getQueryParam('molecules').pipe(
      filter(queryParamValue => queryParamValue !== undefined),
      filter(queryParamValue => queryParamValue !== this.frequencyViewChartFilterService.getChartFilter('molecules')?.value),
      tap(value => this.moleculesFilterValue.next(value))
    ).subscribe();

    this.asaxFrequencyViewService.getRedshiftEstimated().pipe(
      tap((redshift => this.redshiftEstimated.next(redshift)))
    ).subscribe();
  }

  onOutFilter(headerFilter: HeaderFilter): void {
    this.frequencyViewChartFilterService.upsertChartFilter(headerFilter);

    if (!!headerFilter.value) {
      this.asaxBookmarkingService.addParamsToUrl({[headerFilter.id]: headerFilter.value});
    } else {
      this.asaxBookmarkingService.removeParamsFromUrl(headerFilter.id);
    }
  }

  onOutRedshiftFilter(headerFilter: HeaderFilter): void {
    this.frequencyViewChartFilterService.upsertChartFilter(headerFilter);

    if (this.redshiftMode === 'estimated') {
      this.asaxBookmarkingService.removeParamsFromUrl(headerFilter.id);
    } else {
      if (!!headerFilter.value) {
        this.asaxBookmarkingService.addParamsToUrl({[headerFilter.id]: headerFilter.value});
      } else {
        this.asaxBookmarkingService.removeParamsFromUrl(headerFilter.id);
      }
    }
  }

  onOutRedshiftMode(redshiftMode: RedshiftMode): void {
    this.redshiftMode = redshiftMode;
  }

  resetBoundaries() {
    this.displayResetBoundariesButton = false;
    this.asaxFrequencyViewService.setResetExtremes(this.extremes);
  }
}
